import PageMeta from "@/components/PageMeta";
import { useState } from "react";
import {
  Send,
  MapPin,
  Clock,
  Shield,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import { submitQuote } from "@/lib/api";
import SectionFadeIn from "@/components/SectionFadeIn";
import { createInitialQuoteForm, getQuotePrefillSummary } from "@/lib/quotePrefill";
import { trackQuoteSubmit } from "@/lib/analytics";
import QuoteSuccessView from "@/components/quote/QuoteSuccessView";
import QuoteDeliverySection from "@/components/quote/QuoteDeliverySection";
import QuoteAttachments from "@/components/quote/QuoteAttachments";

const PROJECT_TYPES = [
  "Canvas Print",
  "Wall Mural",
  "Custom Sign",
  "Business Signage",
  "Vehicle Wrap",
  "Window Graphics",
  "Other",
];

const trust = [
  { icon: Clock, text: "Response within 24 hours" },
  { icon: Shield, text: "No obligation, no pressure" },
  { icon: MapPin, text: "UK-based team" },
];

export default function RequestQuote() {
  const location = useLocation();
  const [form, setForm] = useState(() => createInitialQuoteForm(location.search));
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const prefillSummary = getQuotePrefillSummary(location.search);

  const update = (field) => (e) =>
    setForm({ ...form, [field]: e.target.value });

  const setDeliveryMethod = (value) => setForm({ ...form, delivery_method: value });

  const addFiles = (e) => {
    const newFiles = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...newFiles].slice(0, 5));
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => formData.append(k, v));
      files.forEach((f) => formData.append("files", f));
      await submitQuote(formData);
      setSubmitted(true);
      trackQuoteSubmit(form.project_type || "Unknown");
    } catch (err) {
      console.error("[RequestQuote] submit failed:", err);
      setError(
        "Something went wrong. Please try again or contact us directly."
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* ── SUCCESS STATE ── */
  if (submitted) {
    return <QuoteSuccessView />;
  }

  /* ── FORM FIELD STYLES ── */
  const fieldBase =
    "w-full bg-transparent border-b border-brand-border focus:border-brand-gold text-brand-text py-3 outline-none transition-colors duration-300 placeholder:text-brand-text-secondary/30 text-[14px]";

  const sectionLabel = (text) => (
    <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-brand-gold mb-6">
      {text}
    </p>
  );

  const fieldLabel = (text, required) => (
    <label className="block text-[11px] tracking-wide uppercase text-brand-text-secondary/70 mb-2">
      {text}
      {required && <span className="text-brand-gold ml-0.5">*</span>}
    </label>
  );

  /* ── FORM PAGE ── */
  return (
    <div className="py-20 md:py-28" data-testid="request-quote-page">
      <PageMeta
        title="Request a Free Quote"
        description="Tell us about your project — canvas, murals, stickers, banners, vehicle graphics, or custom signage. We'll review your brief and come back with an accurate quote. No obligation."
      />
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        {/* Header */}
        <SectionFadeIn>
          <p className="text-[11px] tracking-[0.35em] uppercase font-semibold text-brand-gold mb-3">
            Free Quote &mdash; No Obligation
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-brand-text">
            Tell Us About Your Project
          </h1>
          <p className="mt-3 text-[14px] text-brand-text-secondary max-w-lg leading-relaxed">
            Fill in what you can. Attach any references you have. We&rsquo;ll
            come back with a clear price and timeline.
          </p>
        </SectionFadeIn>

        {/* Trust indicators */}
        <div className="mt-8 mb-10 flex flex-wrap gap-x-8 gap-y-3">
          {trust.map((t) => (
            <div key={t.text} className="flex items-center gap-2">
              <t.icon
                size={15}
                strokeWidth={1.6}
                className="text-brand-gold"
              />
              <span className="text-[12px] text-brand-text-secondary/70 tracking-wide">
                {t.text}
              </span>
            </div>
          ))}
        </div>

        {prefillSummary && (
          <SectionFadeIn>
            <div
              className="mb-10 rounded-sm border border-brand-gold/30 bg-brand-gold/5 px-5 py-4"
              data-testid="quote-prefill-banner"
            >
              <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-gold">
                Prefilled from your selection
              </p>
              <p className="mt-2 text-[13px] leading-relaxed text-brand-text-secondary" data-testid="quote-prefill-summary">
                {prefillSummary}
              </p>
            </div>
          </SectionFadeIn>
        )}

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          data-testid="quote-form"
        >
          {/* ── About You ── */}
          {sectionLabel("About You")}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
            <div>
              {fieldLabel("Full Name", true)}
              <input
                type="text"
                required
                value={form.name}
                onChange={update("name")}
                className={fieldBase}
                placeholder="Your full name"
                data-testid="quote-name"
              />
            </div>
            <div>
              {fieldLabel("Email", true)}
              <input
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                className={fieldBase}
                placeholder="you@example.com"
                data-testid="quote-email"
              />
            </div>
            <div>
              {fieldLabel("Phone", false)}
              <input
                type="tel"
                value={form.phone}
                onChange={update("phone")}
                className={fieldBase}
                placeholder="+44 ..."
                data-testid="quote-phone"
              />
            </div>
            <div>
              {fieldLabel("Project Type", true)}
              <select
                required
                value={form.project_type}
                onChange={update("project_type")}
                className={`${fieldBase} bg-brand-bg cursor-pointer`}
                data-testid="quote-project-type"
              >
                <option value="" disabled>
                  Select a type
                </option>
                {PROJECT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* ── Your Project ── */}
          <div className="mt-12 pt-10 border-t border-white/[0.04]">
            {sectionLabel("Your Project")}
            <div>
              {fieldLabel("Description", true)}
              <textarea
                required
                rows={4}
                value={form.description}
                onChange={update("description")}
                className="w-full bg-transparent border border-brand-border focus:border-brand-gold text-brand-text py-3 px-4 outline-none transition-colors duration-300 placeholder:text-brand-text-secondary/30 text-[14px] resize-none rounded-sm"
                placeholder="What do you need? Include size, materials, purpose, or any references."
                data-testid="quote-description"
              />
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-7">
              <div>
                {fieldLabel("Dimensions", false)}
                <input
                  type="text"
                  value={form.dimensions}
                  onChange={update("dimensions")}
                  className={fieldBase}
                  placeholder="e.g. 100cm x 80cm"
                  data-testid="quote-dimensions"
                />
              </div>
              <div>
                {fieldLabel("Quantity", false)}
                <input
                  type="text"
                  value={form.quantity}
                  onChange={update("quantity")}
                  className={fieldBase}
                  placeholder="e.g. 1"
                  data-testid="quote-quantity"
                />
              </div>
              <div>
                {fieldLabel("Deadline", false)}
                <input
                  type="text"
                  value={form.deadline}
                  onChange={update("deadline")}
                  className={fieldBase}
                  placeholder="e.g. 2 weeks"
                  data-testid="quote-deadline"
                />
              </div>
            </div>
          </div>

          {/* ── Delivery ── */}
          <div className="mt-12 pt-10 border-t border-white/[0.04]">
            {sectionLabel("Delivery")}
            <QuoteDeliverySection form={form} onUpdate={update} onSetMethod={setDeliveryMethod} />
          </div>

          {/* ── Attachments ── */}
          <div className="mt-12 pt-10 border-t border-white/[0.04]">
            {sectionLabel("Attachments")}
            <QuoteAttachments files={files} onAddFiles={addFiles} onRemoveFile={removeFile} />
          </div>

          {/* ── Submit ── */}
          {error && (
            <p
              className="mt-8 text-red-400 text-[13px]"
              data-testid="quote-error"
            >
              {error}
            </p>
          )}

          <div className="mt-10 pt-8 border-t border-white/[0.04] flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 bg-brand-gold text-brand-bg hover:bg-brand-gold-hover transition-colors duration-300 rounded-sm px-9 py-3.5 font-medium text-[13px] tracking-wide disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="quote-submit"
            >
              {submitting ? "Sending\u2026" : "Send Quote Request"}
              <Send size={15} />
            </button>
            <span className="text-[11px] text-brand-text-secondary/60 leading-snug">
              We&rsquo;ll respond within 24 hours.
              <br />
              No obligation, no spam.
            </span>
          </div>
        </form>
      </div>
    </div>
  );
}
